import { useEffect, useState } from "react";

import { Box, Button, Divider, FormControl, FormErrorMessage, FormLabel, Img, Input, Switch, useDisclosure, useToast } from "@chakra-ui/react"
import { Field, FieldProps, Form, Formik } from "formik"
import { useLocation, useNavigate } from "react-router-dom"
import { HttpStatusCode } from "axios";

import ArrowIcon from "@/assets/ArrowIcon";
import EditIcon from "@/assets/EditIcon";
import { COLORS, DURATION_TOAST } from "@/common/constants";
import FileUploader from "@/components/Fields/FileUploader";
import ModalConfirmation from "@/components/Modals/ModalConfirmation";
import CKEditorComponent from "@/components/Fields/CKEditorComponent";
import { invoiceTemplateSchema } from "@/schemas/invoiceTemplateSchema";
import { getInvoiceTemplateById, postInvoiceTemplate, putInvoiceTemplate } from "@/services/invoiceTemplateService";

const EditInvoiceTemplate = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const toast = useToast();

    const { id } = location.state || {};

    const [approvalFile, setApprovalFile] = useState<File | null>(null);
    const [confirmationText, setConfirmationTest] = useState("");
    const [confirmButtonText, setConfirmButtonText] = useState("");
    const [cancelButtonText, setCancelButtonText] = useState("");
    const [dataDetail, setDataDetail] = useState<any>(null);
    const [dataEdit, setDataEdit] = useState<any>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [previewFile, setPreviewFile] = useState('');
    const [showEditImage, setShowEditImage] = useState(false);
    const [title, setTitle] = useState("");

    const handleCancel = () => {
        onOpen();
        setTitle("Cancel Changes?");
        setConfirmationTest("Are you sure you want to discard all changes to this template?");
        setConfirmButtonText("Yes");
        setCancelButtonText("No");
    }

    const generateDataEdit = () => {
        const data = new FormData();
        data.append('templateName', dataEdit.templateName);
        data.append('contactPerson', dataEdit.contactPerson);
        data.append('indonesiaBank', dataEdit.indonesiaBank);
        data.append('internationalBank', dataEdit.internationalBank);
        data.append('managerName', dataEdit.managerName);
        data.append('file', approvalFile ?? '');
        data.append('isDefaultTemplate', dataEdit.isDefaultTemplate);
        if (id){
            data.append('templateId', id);
            data.append('ApprovalFile', dataDetail.approvalFile);
        }
        // for (let [key, value] of data.entries()) {
        //     console.log(`${key}: ${value}`);
        // }
        return data;
    };

    const handleSubmit = async (data: any) => {
        setTitle("Save Template?");
        setConfirmationTest("Are you sure you want to save this template?");
        setConfirmButtonText("Save Template");
        setCancelButtonText("Cancel");
        onOpen();
        setDataEdit(data);
        setIsSubmitting(true);
    }

    const handleConfirm = async () => {
        onClose();

        if (title.startsWith("Cancel")) {
            //navigate('/settings/template');
            history.back();
            return;
        }

        const obj = generateDataEdit();
        let response;
        if (id){
            response = await putInvoiceTemplate(obj);
        } else {
            response = await postInvoiceTemplate(obj);
        }
        setIsSubmitting(false);
        if (response.status === HttpStatusCode.Ok) {
            toast({
                title: 'Template saved.',
                description: "We've saved the template for you.",
                status: 'success',
                duration: DURATION_TOAST,
                isClosable: true,
            });

            setTimeout(() => {
                navigate('/settings/template')
            }, 1000)
        } else {
            toast({
                title: 'Template saving failed.',
                description: "We could not save the template for you.",
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
        }
    }

    const handleDrop = (acceptedFiles: File[], setFieldValue: any) => {
        setApprovalFile(acceptedFiles[0]);
        if (acceptedFiles[0]){
            setPreviewFile(URL.createObjectURL(acceptedFiles[0]));
        }
        setFieldValue('approvalFile', acceptedFiles[0]);
    };

    const handleConfirmToCancel = () => {
        setIsSubmitting(false);
        onClose();
    }

    const handleShowEditImage = () => {
        setShowEditImage(!showEditImage);
    }

    const getDataDetail = async () => {
        const response = await getInvoiceTemplateById(id) as any;
        if (response) {
            setDataDetail(response.data);
            if (response.data && response.data?.uriFile) {
                setPreviewFile(response.data.uriFile);
            } else {
                setPreviewFile('');
            }
        }
    }

    useEffect(() => {
        if (id) {
            getDataDetail();
        }
    }, [id])

    return (
        <>
            <ModalConfirmation isOpen={isOpen} onClose={onClose} title={title} handleCancel={handleConfirmToCancel} handleConfirm={handleConfirm} confirmationText={confirmationText} confirmButtonText={confirmButtonText} cancelButtonText={cancelButtonText} />
            <div className="form-container">
                <Formik
                    initialValues={{
                        templateName: (dataDetail?.templateName ?? "") as string,
                        contactPerson: (dataDetail?.contactPerson ?? "") as string,
                        indonesiaBank: (dataDetail?.indonesiaBank ?? "") as string,
                        internationalBank: (dataDetail?.internationalBank ?? "") as string,
                        managerName: (dataDetail?.managerName ?? "") as string,
                        approvalFile: (dataDetail?.uriFile ?? "") as (string | Blob),
                        isDefaultTemplate: dataDetail?.isDefaultTemplate as boolean,
                    }}
                    validationSchema={invoiceTemplateSchema(Boolean(id))}
                    onSubmit={(values) => {
                        handleSubmit(values);
                    }}
                    enableReinitialize
                >
                    {(props) => (
                        <Form>
                            <Field name='templateName'>
                                {({ field, form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.templateName && form.touched.templateName)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Template Name<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div className="form-input-container">
                                                <Input {...field} placeholder='Template Name' className="form-input" isDisabled={isSubmitting} />
                                                <FormErrorMessage>{String(form.errors.templateName)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='contactPerson'>
                                {({ form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.contactPerson && form.touched.contactPerson)} style={{ marginBottom: 40 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Contact Person<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div className="form-input-container">
                                                <CKEditorComponent
                                                    value={props.values.contactPerson}
                                                    onChange={(value) => props.setFieldValue('contactPerson', value)}
                                                    isDisabled={isSubmitting}
                                                />
                                                <FormErrorMessage style={{ marginTop: '30px' }}>{String(form.errors.contactPerson)}</FormErrorMessage>
                                            </div>
                                        </div>
                                        
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='indonesiaBank'>
                                {({ form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.indonesiaBank && form.touched.indonesiaBank)} style={{ marginBottom: 40 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Indonesia Bank<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div className="form-input-container">
                                                <CKEditorComponent
                                                    value={props.values.indonesiaBank}
                                                    onChange={(value) => props.setFieldValue('indonesiaBank', value)}
                                                    isDisabled={isSubmitting}
                                                />
                                                <FormErrorMessage style={{ marginTop: '30px' }}>{String(form.errors.indonesiaBank)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='internationalBank'>
                                {({ form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.internationalBank && form.touched.internationalBank)} style={{ marginBottom: 40 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">International Bank<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div className="form-input-container">
                                                <CKEditorComponent
                                                    value={props.values.internationalBank}
                                                    onChange={(value) => props.setFieldValue('internationalBank', value)}
                                                    isDisabled={isSubmitting}
                                                />
                                                <FormErrorMessage style={{ marginTop: '30px' }}>{String(form.errors.internationalBank)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='managerName'>
                                {({ field, form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.managerName && form.touched.managerName)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Manager Name<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div className="form-input-container">
                                                <Input {...field} placeholder='Manager Name' className="form-input" isDisabled={isSubmitting} />
                                                <FormErrorMessage>{String(form.errors.managerName)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            {id ? <Field name='approvalFile'>
                                {({ form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.approvalFile && form.touched.approvalFile)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Approval File<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div className="form-input-container">
                                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                                    <Box
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            padding: 10,
                                                            border: '1px solid #EAECF0',
                                                            borderRadius: '13px',
                                                        }}
                                                    >
                                                        {previewFile && <Img
                                                            src={previewFile}
                                                            style={{
                                                                maxHeight: 200,
                                                                maxWidth: '90%',
                                                                height: 'auto',
                                                                width: 'auto',
                                                                objectFit: 'contain',
                                                            }}
                                                        />}
                                                        <EditIcon style={{ marginLeft: 10, cursor: 'pointer' }} onClick={() => handleShowEditImage()} />
                                                    </Box>
                                                </div>
                                                {showEditImage &&
                                                    <FileUploader onDrop={(e) => handleDrop(e, props.setFieldValue)} isInvalid={Boolean(form.errors.uploadIDCard && form.submitCount)} type="template" isDisabled={isSubmitting} />
                                                }
                                                <FormErrorMessage>{String(form.errors.approvalFile)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            :
                            <Field name='approvalFile'>
                                {({ form }: any) => (
                                    <FormControl isInvalid={form.errors.approvalFile && form.submitCount} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Approval File<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div className="form-input-container">
                                                <FileUploader onDrop={(e) => handleDrop(e, props.setFieldValue)} isInvalid={form.errors.approvalFile && form.submitCount} type="template" isDisabled={props.isSubmitting} />
                                                <FormErrorMessage>{form.errors.approvalFile}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>}
                            <Field name='isDefaultTemplate'>
                                {({ field, form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.isDefaultTemplate && form.touched.isDefaultTemplate)} style={{ marginBottom: 15, alignItems: 'center' }}>
                                        <div className="form-field-container">
                                            <FormLabel htmlFor='status-switch' className="form-label">
                                                Default Template
                                            </FormLabel>
                                            <div className="form-input-container">
                                                <Switch {...field} isChecked={field.value} onChange={(e) => props.setFieldValue('isDefaultTemplate', e.target.checked)} id='isDefaultTemplate-switch' style={{ width: '50%', marginTop: '5px', backgroundColor: 'white' }} isDisabled={isSubmitting} />
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Divider />
                            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <Button
                                    mt={4}
                                    bgColor='white'
                                    borderColor='#D0D5DD'
                                    isLoading={isSubmitting}
                                    marginRight={5}
                                    onClick={handleCancel}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    mt={4}
                                    bgColor={COLORS.BUTTON.BACKGROUND}
                                    color={COLORS.BUTTON.FOREGROUND}
                                    isLoading={isSubmitting}
                                    type='submit'
                                    rightIcon={<ArrowIcon />}
                                >
                                    Save Template
                                </Button>
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>
        </>
    )
}

export default EditInvoiceTemplate;
